import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import dayjs from 'dayjs';
import {LeadStateModel} from '@/typings/models/LeadState';

export const sortByKey = (array, key) => {
  return array.sort(function(a, b) {
    const x = a[key];
    const y = b[key];
    return ((x < y) ? -1 : ((x > y) ? 1 : 0));
  });
};

export const emptyFunction = () => {
};


// TODO: Enhance Duplicate Code with Lightweight Launcher
export const extractPathVariableParts = (schema = '') => {
  const splittedBySlash = schema.split('/').filter(part => part.length > 0);
  return splittedBySlash.map(part => {
    if (part.startsWith(':')) {
      return {
        type: 'param',
        value: part.split(':')[1],
      };
    }
    return {
      type: 'const',
      value: part,
    };
  });
};

type IURLParamBuilderOptions = {
  params?: Record<string, unknown>
  queryString?: Record<string, unknown>,
  root?: string
  thirdParty?: boolean
}

const AddPathVariableFromParams = (params: Record<string, unknown>, part: {
  type?: string,
  value?: any
}) => params?.[part.value] ? `/${params?.[part.value]}` : '';

const addPathVariableToURL = (url, schemaParts: { type?: string, value?: any }[], params: Record<string, unknown>) => {
  for (const part of schemaParts.filter(part => part.type && part.value)) {
    url += part.type === 'param' ? AddPathVariableFromParams(params, part) : `/${part.value}`;
  }
  return url;

};
const addQueryStringToUrl = (url: string, queryString: Record<string, unknown>) => {
  const queryStringEntries = Object.entries(queryString).filter(([_, value]) => value !== undefined);
  // Percent-encode values so search terms with special chars (spaces, `+`, `&`, `@`…) survive transit.
  // Matches the encoding already done on the pagination-link path (`URL.searchParams.set`) and `buildUrl`.
  const queryStringString = queryStringEntries.map(([key, value], index) => `${index === 0 ? '?' : '&'}${key}=${encodeURIComponent(String(value))}`).join('');
  url += queryStringString;
  return url;

};
const addRootToUrl = (root: string) => {
  if (!root || typeof root !== 'string') {
    root = '';
  }
  if (root?.endsWith('/'))
    root = root?.substring(0, root?.length - 1);

  return root;

};

export const URLParamBuilder = (path: string, options: IURLParamBuilderOptions = {}, separateQueryStringsWithSlash = true) => {
  const { queryString = {}, params = {} } = options;
  let { root, thirdParty } = options;

  let url = thirdParty ? path : addRootToUrl(root);

  const schemaParts = extractPathVariableParts(path);
  url = addPathVariableToURL(url, schemaParts, params);
  url += separateQueryStringsWithSlash ? '/' : '';

  url = addQueryStringToUrl(url, queryString);
  return url;
};


export function getExportURL(list: any): string | boolean {
  try {
    return list.links.export || false;
  } catch (e) {
    return false;
  }
}

export function capitalizeFirstLetter(str = '') {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function capitalizeAllLetters(str = '') {
  return str.toUpperCase();
}


export function getInitials(string = '', count = 2) {
  try {
    return string.split(' ').slice(0, count).map(str => capitalizeFirstLetter(str[0])).join('');
  } catch (e) {
    return 'CV';
  }
}


export function saveChartData(state: any, key: string, payload: any) {
  return {
    ...state,
    [key]: payload.data,
  };
}

export const parseSort = (sortString = '') => {
  if (!sortString) return {};
  const sort_direction = sortString.startsWith('-') ? 'desc' : 'asc';
  const sort_by = sortString.replace('-', '').replace('+', '');
  return { sort_by, sort_direction };
};

/**
 * Turns an antd/ProTable sorter into the signed sort string `parseSort` reads
 * (`+field` / `-field`). Returns '' when the sort was cleared, which callers
 * should treat as "let the API use its own default order".
 */
export const sorterToSortString = (sorter: any): string => {
  const active = Array.isArray(sorter) ? sorter[0] : sorter;
  const order = active?.order;
  // columnKey wins: a column's API sort name may differ from its dataIndex.
  const field = active?.columnKey || active?.field;
  if (!order || !field || Array.isArray(field)) return '';
  return `${order === 'ascend' ? '+' : '-'}${field}`;
};

/** The antd `sortOrder` a column should show for a given signed sort string. */
export const sortOrderForColumn = (
  sortString: string,
  columnKey: string,
): 'ascend' | 'descend' | null => {
  if (!sortString || sortString.slice(1) !== columnKey) return null;
  return sortString.startsWith('-') ? 'descend' : 'ascend';
};

export const filterNullUndefined = (obj: any) => {
  if (typeof obj !== 'object' || obj === null) return obj;

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const filteredValue = filterNullUndefined(value); // Recursively process nested objects/arrays

    if (filteredValue !== null && filteredValue !== undefined) {
      acc[key] = filteredValue;
    }

    return acc;
  }, Array.isArray(obj) ? [] : {});
};


export function hexToRgba(hex: string, opacity: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function setColorOpacity(color: string, opacity: number) {
  if (isEmpty(color)) return '';

  const R = parseInt(color.length === 4 ? color.substring(1, 2) : color.substring(1, 3), 16);
  const G = parseInt(color.length === 4 ? color.substring(2, 3) : color.substring(3, 5), 16);
  const B = parseInt(color.length === 4 ? color.substring(3, 4) : color.substring(5, 7), 16);

  return `rgba(${R}, ${G}, ${B}, ${opacity / 100})`;
}

// Query parameter utility types
export interface QueryParameterMapping {
  [queryParam: string]: {
    filterKey: string | string[];
    isArray?: boolean;
    transform?: (value: string) => any;
  };
}

export interface ParseQueryParametersOptions {
  excludeParams?: string[];
  parameterMappings?: QueryParameterMapping;
}

export interface ParsedQueryParameters {
  hasFilterParams: boolean;
  filterPayload: Record<string, any>;
  shouldClearSavedView: boolean;
}

/**
 * Parses URL query parameters and converts them to filter payload
 * @param searchString - The URL search string (e.g., "?assignee=user123&status=new")
 * @param place - The filter place identifier for Redux state
 * @param options - Configuration options for parameter mapping and exclusions
 * @returns Parsed query parameters with filter payload and metadata
 */
export function parseQueryParametersForFilters(
  searchString: string,
  place: string,
  options: ParseQueryParametersOptions = {},
): ParsedQueryParameters {
  const {
    excludeParams = ['redirect', 'tab', 'modal'],
    parameterMappings = {},
  } = options;

  const params = new URLSearchParams(searchString || '');

  // Check if any query parameters exist (excluding navigation params)
  const allParams = Array.from(params.keys()).filter(key =>
    !excludeParams.includes(key.toLowerCase()),
  );

  if (allParams.length === 0) {
    return {
      hasFilterParams: false,
      filterPayload: {},
      shouldClearSavedView: false,
    };
  }

  // Build filter payload based on query parameters
  const filterPayload: Record<string, any> = {
    place,
  };

  // Default parameter mappings for common lead management filters
  const defaultMappings: QueryParameterMapping = {
    // Location parameters
    'branch': { filterKey: 'branch', isArray: true },
    'branch_id': { filterKey: 'branch', isArray: true },
    'branches': { filterKey: 'branch', isArray: true },

    // Service Type parameters
    'service_type': { filterKey: 'service_type', isArray: true },
    'service_types': { filterKey: 'service_type', isArray: true },

    // Assignee parameters
    'assignee_id': { filterKey: 'assignee', isArray: false },
    'assignee': { filterKey: 'assignee', isArray: false },
    'assignees': { filterKey: 'assignee', isArray: true },

    // Lead state parameters
    'lead_state': { filterKey: 'leadState', isArray: false },
    'status': { filterKey: 'leadState', isArray: false },

    // Lead source parameters
    'lead_source': { filterKey: 'leadSource', isArray: false },
    'source': { filterKey: 'leadSource', isArray: false },

    // Priority parameters
    'priority': { filterKey: 'priority', isArray: false },

    // Date parameters
    'created_from': { filterKey: 'createdFrom' },
    'created_to': { filterKey: 'createdTo' },
    'modified_from': { filterKey: 'modifiedFrom' },
    'modified_to': { filterKey: 'modifiedTo' },

    'start_date': { filterKey: ['date', 'start'], transform: (dateString) => dayjs(dateString).unix() * 1000 },
    'end_date': { filterKey: ['date', 'end'], transform: (dateString) => dayjs(dateString).unix() * 1000 },

    ...parameterMappings,
  };

  // Process each query parameter
  for (const [paramKey, paramValue] of params.entries()) {
    if (excludeParams.includes(paramKey.toLowerCase()) || !paramValue) {
      continue;
    }

    const mapping = defaultMappings[paramKey];
    if (mapping) {
      const { filterKey, isArray = false, transform } = mapping;

      // Skip if this filter key was already set by a higher priority parameter
      if (filterPayload[filterKey]) {
        continue;
      }

      let processedValue: any = decodeURIComponent(paramValue.trim());

      // Apply transformation if provided
      if (transform) {
        processedValue = transform(processedValue);
      }

      // Handle array values (comma-separated)
      if (isArray) {
        processedValue = processedValue
          .split(',')
          .map((s: string) => decodeURIComponent(s.trim()))
          .filter(Boolean);
      }

      set(filterPayload, filterKey, processedValue);
    }
  }

  return {
    hasFilterParams: true,
    filterPayload,
    shouldClearSavedView: true,
  };
}

export function extractMagicFieldsFromLeadState(leadState: LeadStateModel): {optional?: string[], mandatory?: string[]} {
  const configuration = leadState?.meta?.magicFields
  const magicFields = {}
  Object.entries(configuration || {}).forEach(([field, magicFieldType]) => {
    if (!magicFields[magicFieldType])
      magicFields[magicFieldType] = []

    magicFields[magicFieldType].push(field)
  })

  return magicFields
}

type Rgb = { r: number; g: number; b: number };

const expandShortHex = (hex: string) =>
  hex.length === 3 || hex.length === 4
    ? hex
        .slice(0, 3)
        .split('')
        .map(char => char + char)
        .join('')
    : hex.slice(0, 6);

/**
 * Parse the color formats a backend/integration payload realistically sends us
 * (#rgb, #rgba, #rrggbb, #rrggbbaa, rgb()/rgba()). Anything else — named
 * colors, hsl(), gradients, garbage — returns null so callers can keep their
 * own default instead of computing with NaN.
 */
export function parseColorToRgb(color?: string | null): Rgb | null {
  if (!color || typeof color !== 'string') return null;
  const value = color.trim().toLowerCase();

  if (value.startsWith('#')) {
    const hex = value.slice(1);
    if (![3, 4, 6, 8].includes(hex.length) || /[^0-9a-f]/.test(hex)) return null;
    const full = expandShortHex(hex);
    return {
      r: parseInt(full.slice(0, 2), 16),
      g: parseInt(full.slice(2, 4), 16),
      b: parseInt(full.slice(4, 6), 16),
    };
  }

  const rgbMatch = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch.slice(1, 4).map(Number);
    if ([r, g, b].some(channel => !Number.isFinite(channel) || channel < 0 || channel > 255)) return null;
    return { r, g, b };
  }

  return null;
}

/** WCAG 2.1 relative luminance (0 = black, 1 = white). */
export function getRelativeLuminance(color?: string | null): number | null {
  const rgb = parseColorToRgb(color);
  if (!rgb) return null;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(channel => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Whether dark text reads better than white text on `background`. Returns false
 * for a background we can't parse, so callers keep their existing light text.
 *
 * Uses a luminance threshold rather than maximizing the WCAG contrast ratio:
 * ratio-maximizing flips at luminance 0.179, which would put dark text on
 * saturated mid-tones like antd blue (#1677ff) where white is the convention.
 * 0.4 only flips fills that are genuinely light (pastels, ambers, whites).
 */
export function prefersDarkText(background?: string | null, threshold = 0.4) {
  const luminance = getRelativeLuminance(background);
  if (luminance === null) return false;
  return luminance > threshold;
}
